import { AffiliationRelations, AffiliationTypes } from '@generated/graphql';
import type { Affiliation } from '@interfaces/affiliation';
import { ClientSelectField } from '@ovComponents/2-component/clientSelectField/clientSelect';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { enumValues } from '@utils/enumValues';
import { Box, Button, Grid } from 'hephaestus/web';
import { AddRounded as AddRoundedIcon } from 'hephaestus/webIcons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BeneficiaryTable } from './beneficiaryTable';

const initBeneficiary = {
  allocation: 0,
  id: '',
  relation: AffiliationRelations.Child,
  signatureRequired: false,
  type: AffiliationTypes.PrimaryBeneficiary,
};

export const BeneficiariesCrud = ({
  beneficiaries,
  setBeneficiaries,
  disabled,
}: {
  beneficiaries: Affiliation[];
  setBeneficiaries: (beneficiaries: Affiliation[]) => void;
  hideAllocation?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation([
    'workflowCompletions',
    'affiliationTypes',
    'components',
  ]);

  const [beneficiary, setBeneficiary] =
    useState<Partial<Affiliation>>(initBeneficiary);

  /* clean when disabled */
  useEffect(() => {
    if (disabled === true) {
      setBeneficiaries([]);
    }
  }, [disabled]);

  const validateBeneficiary = (): boolean => {
    if (beneficiary && beneficiary.user?.id && beneficiary.relation)
      return true;
    return false;
  };

  const addOrUpdateBeneficiary = (): void => {
    if (!validateBeneficiary()) return;
    if (
      beneficiaries.some(
        (item) => beneficiary.user && beneficiary.user.id === item.user?.id,
      )
    ) {
      const updatedBeneficiaries = beneficiaries.map((item) => {
        if (beneficiary.user && beneficiary.user.id === item.user?.id)
          return beneficiary as Affiliation;
        return item;
      });

      setBeneficiaries([...updatedBeneficiaries]);
    } else {
      setBeneficiaries([...beneficiaries, beneficiary as Affiliation]);
    }
  };
  return (
    <>
      <BeneficiaryTable
        beneficiaries={beneficiaries as Affiliation[]}
        setBeneficiaries={setBeneficiaries}
      />
      <Grid
        container
        spacing={1}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 2,
          mt: 1,
        }}
      >
        <Grid size={4}>
          <ClientSelectField
            label={t('components:beneficiaryCrud.beneficiaryName')}
            user={beneficiary.user}
            setUser={(item) => setBeneficiary({ ...beneficiary, user: item })}
            fullWidth
            disabled={disabled}
            autoFocus
          />
        </Grid>
        <Grid size={5}>
          <SelectField
            onChange={(e: any) =>
              setBeneficiary({ ...beneficiary, relation: e.target.value })
            }
            label={t('components:beneficiaryCrud.relationshipToAccountHolder')}
            fullWidth
            value={beneficiary.relation}
            disabled={disabled}
          >
            {enumValues(AffiliationRelations)
              .filter(
                (item) => item !== 'PRIMARY_CAREGIVER' && item !== 'GUARDIAN',
              )
              .map((relation, index) => (
                <MenuItem key={index} value={relation}>
                  {t(`affiliationTypes:affiliationRelationship.${relation}`)}
                </MenuItem>
              ))}
          </SelectField>
        </Grid>
        <Grid size={3}>
          <TextField
            type='number'
            onChange={(e: any) =>
              setBeneficiary({
                ...beneficiary,
                allocation: Number(e.target.value),
              })
            }
            label={t('components:beneficiaryCrud.allocation')}
            fullWidth
            sx={{ mt: 1 }}
            value={beneficiary.allocation}
            trailingIcon='percent'
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'start',
        }}
      >
        <Button
          variant='outlined'
          startIcon={<AddRoundedIcon />}
          disabled={!validateBeneficiary()}
          onClick={() => addOrUpdateBeneficiary()}
          type='button'
        >
          {t('components:beneficiaryCrud.addAffiliate')}
        </Button>
      </Box>
    </>
  );
};
